import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import {useMachine} from '@xstate/react';
import React, {useCallback, useEffect, useMemo} from 'react';

import * as analytics from 'analytics/Analytics';
import {AnalyticsEvent} from 'analytics/Analytics';
import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {useAntibodyFormatsQuery} from 'antibodies/hooks/useAntibodyFormatsQuery';
import {TranslationParameters} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import AntibodyNestedDataCreationSpreadsheetImportModalMachine from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportModalMachine';
import {getCountsOfCreatedComponents} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  AntibodySpreadsheetImportStepKey,
  ANTIBODY_SPREADSHEET_IMPORT_STEPS,
  NdcGermlineConfig,
  NdcRelatedSchemasConfig,
  NdcStartingType,
  NdcSpreadsheetInterpretation,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import ColumnMappingPane from 'antibodies/nested-data-creation/spreadsheet-import/panes/ColumnMappingPane';
import FinalizePane from 'antibodies/nested-data-creation/spreadsheet-import/panes/FinalizePane';
import SelectFormatPane from 'antibodies/nested-data-creation/spreadsheet-import/panes/SelectFormatPane';
import UploadSpreadsheetPane from 'antibodies/nested-data-creation/spreadsheet-import/panes/UploadSpreadsheetPane';
import ValidationErrorsPane from 'antibodies/nested-data-creation/spreadsheet-import/panes/ValidationErrorsPane';
import {assertNotNullAndReturn} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  AnalyticsMetadataForContext,
  AnalyticsModalType,
} from 'constants/AnalyticsConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useOnMount from 'ui-library/hooks/useOnMount';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant, StepNavSize, StepItemStatus} from 'ui-library/StepNav';
import {assertNever} from 'util/Types';

/** Validation errors step is only shown if there are validation errors upon submission */
const ALWAYS_SHOWN_ANTIBODY_SPREADSHEET_IMPORT_STEPS = ANTIBODY_SPREADSHEET_IMPORT_STEPS.filter(
  (step) => step.key !== AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS
);

interface NestedCreateAndRegisterAntibodiesSpreadsheetImportModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  initialProjectFolderId?: string | null;
  analyticsMetadata: AnalyticsMetadataForContext;
}

const useStyles = createStyles((theme) => ({
  modalBody: {
    padding: '24px',
  },
  stepNav: {
    marginBottom: '16px',
  },
  unsupportedConfigBanner: {
    marginBottom: '16px',
  },
}));

export default function NestedCreateAndRegisterAntibodiesSpreadsheetImportModal({
  onRequestClose,
  onRequestSubmit,
  initialProjectFolderId,
  analyticsMetadata,
}: NestedCreateAndRegisterAntibodiesSpreadsheetImportModalProps): JSX.Element {
  const {classes} = useStyles();
  const antibodyFormatsQuery = useAntibodyFormatsQuery();
  const rolloutFlags = useRolloutFlags();
  const {molbioEnableAntibodyAwareness} = rolloutFlags;
  const showGermlineSelectors = molbioEnableAntibodyAwareness === true;
  const [ndcSpreadsheetImportModalState, send] = useMachine(
    AntibodyNestedDataCreationSpreadsheetImportModalMachine,
    {
      input: {initialProjectFolderId, germlineSelectorsEnabled: showGermlineSelectors},
    }
  );

  const selectedAntibodyFormat: AntibodyFormat | null = useMemo(() => {
    if (
      antibodyFormatsQuery.status !== QueryResultType.SUCCESS ||
      !ndcSpreadsheetImportModalState.context.selectedFormatId
    ) {
      return null;
    }
    const selectedFormat = antibodyFormatsQuery.antibodyFormats.find(
      ({id}) => id === ndcSpreadsheetImportModalState.context.selectedFormatId
    );
    return assertNotNullAndReturn(selectedFormat);
  }, [
    antibodyFormatsQuery.status,
    antibodyFormatsQuery.antibodyFormats,
    ndcSpreadsheetImportModalState.context.selectedFormatId,
  ]);

  const trackEventWithModule = useCallback(
    (event: AnalyticsEvent, additionalMetadata: Record<string, unknown> = {}): void => {
      const metadata: Record<string, unknown> = {
        ...analyticsMetadata,
        modalType: AnalyticsModalType.ANTIBODY_NESTED_DATA_CREATION,
        spreadsheetImport: true,
        formatId: ndcSpreadsheetImportModalState.context.selectedFormatId,
        startingDataType: ndcSpreadsheetImportModalState.context.selectedStartingType,
        numberOfAntibodies:
          ndcSpreadsheetImportModalState.context.spreadsheetInterpretation?.rowCount ?? null,
        ...additionalMetadata,
      };
      analytics.trackEventWithModule(event, ANALYTICS_MODULE.ANTIBODIES, metadata);
    },
    [ndcSpreadsheetImportModalState, analyticsMetadata]
  );

  useOnMount(() => {
    trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL);
  });

  useEffect(() => {
    if (ndcSpreadsheetImportModalState.matches('done')) {
      onRequestSubmit();
      trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL);
    }
  }, [ndcSpreadsheetImportModalState, onRequestSubmit, trackEventWithModule]);

  const getCurrentStep = (): AntibodySpreadsheetImportStepKey => {
    if (ndcSpreadsheetImportModalState.matches('selectFormat')) {
      return AntibodySpreadsheetImportStepKey.SELECT_FORMAT;
    }
    if (ndcSpreadsheetImportModalState.matches('uploadSpreadsheet')) {
      return AntibodySpreadsheetImportStepKey.UPLOAD_SPREADSHEET;
    }
    if (ndcSpreadsheetImportModalState.matches('columnMapping')) {
      return AntibodySpreadsheetImportStepKey.COLUMN_MAPPING;
    }
    if (
      ndcSpreadsheetImportModalState.matches('finalize') ||
      ndcSpreadsheetImportModalState.matches('saving') ||
      ndcSpreadsheetImportModalState.matches('done')
    ) {
      return AntibodySpreadsheetImportStepKey.FINALIZE;
    }
    if (ndcSpreadsheetImportModalState.matches('validationErrors')) {
      return AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS;
    }
    return AntibodySpreadsheetImportStepKey.SELECT_FORMAT;
  };

  const currentStep = getCurrentStep();
  const currentStepIndex = ANTIBODY_SPREADSHEET_IMPORT_STEPS.findIndex((step) => step.key === currentStep);
  /** We hide the validation errors step from nav, unless you're on it, since it is not always shown. */
  const statusByStep: {[key in AntibodySpreadsheetImportStepKey]: StepItemStatus | null} = Object.fromEntries(
    (currentStep === AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS
      ? ANTIBODY_SPREADSHEET_IMPORT_STEPS
      : ALWAYS_SHOWN_ANTIBODY_SPREADSHEET_IMPORT_STEPS
    ).map((step, index) => [
      step.key,
      index <= currentStepIndex ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
    ])
  ) as {[key in AntibodySpreadsheetImportStepKey]: StepItemStatus | null};

  function handleSubmitButton(): void {
    if (currentStep === AntibodySpreadsheetImportStepKey.FINALIZE) {
      send({type: 'submitSpreadsheetImport'});
    } else if (currentStep === AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS) {
      send({type: 'backToUploadSpreadsheet'});
    } else {
      send({type: 'next'});
    }
  }

  function handleOnRequestClose(): void {
    onRequestClose();
    trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, {step: currentStep});
  }

  const handlePrevious = (): void => {
    send({type: 'back'});
  };

  function getButtons(): Array<ModalButton> {
    const buttons: Array<ModalButton> = [
      {
        text: 'Cancel',
        onClick: handleOnRequestClose,
        cancel: true,
        pullLeft: true,
      },
    ];
    if (currentStepIndex !== 0) {
      buttons.push({
        text: 'Back',
        onClick: handlePrevious,
      });
    }

    if (currentStep === AntibodySpreadsheetImportStepKey.SELECT_FORMAT) {
      buttons.push({
        text: 'Next',
        submit: true,
        disabled: !ndcSpreadsheetImportModalState.can({type: 'next'}),
        dataTestAttrs: {
          component: 'NestedCreateAndRegisterAntibodiesSpreadsheetImportModal',
          key: ModalButtonKey.NEXT,
        },
      });
    } else if (currentStep === AntibodySpreadsheetImportStepKey.UPLOAD_SPREADSHEET) {
      buttons.push({
        text: 'Next',
        submit: true,
        disabled: !ndcSpreadsheetImportModalState.can({type: 'next'}),
        dataTestAttrs: {
          component: 'NestedCreateAndRegisterAntibodiesSpreadsheetImportModal',
          key: ModalButtonKey.NEXT,
        },
      });
    } else if (currentStep === AntibodySpreadsheetImportStepKey.COLUMN_MAPPING) {
      buttons.push({
        text: 'Next',
        submit: true,
        disabled: !ndcSpreadsheetImportModalState.can({type: 'next'}),
        dataTestAttrs: {
          component: 'NestedCreateAndRegisterAntibodiesSpreadsheetImportModal',
          key: ModalButtonKey.NEXT,
        },
      });
    } else if (currentStep === AntibodySpreadsheetImportStepKey.FINALIZE) {
      buttons.push({
        text: 'Create and register',
        submit: true,
        disabled: !ndcSpreadsheetImportModalState.can({type: 'submitSpreadsheetImport'}),
        dataTestAttrs: {
          component: 'NestedCreateAndRegisterAntibodiesSpreadsheetImportModal',
          key: ModalButtonKey.SUBMIT,
        },
      });
    } else if (currentStep === AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS) {
      buttons.push({
        text: 'Re-upload spreadsheet',
        submit: true,
        dataTestAttrs: {
          component: 'NestedCreateAndRegisterAntibodiesSpreadsheetImportModal',
          key: 'reuploadSpreadsheetButton',
        },
      });
    } else {
      assertNever(currentStep);
    }
    return buttons;
  }

  // Since the validation errors pane is not always shown, we do not want to include it in the step nav,
  // unless the user is currently on it
  const stepsToShow =
    currentStep === AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS
      ? ANTIBODY_SPREADSHEET_IMPORT_STEPS
      : ALWAYS_SHOWN_ANTIBODY_SPREADSHEET_IMPORT_STEPS;

  const renderSelectFormatPane = (): JSX.Element => (
    <SelectFormatPane
      selectedFormatId={ndcSpreadsheetImportModalState.context.selectedFormatId}
      setSelectedFormat={(format: AntibodyFormat | null) => {
        send({type: 'selectFormat', format});
      }}
      selectedSchemaId={ndcSpreadsheetImportModalState.context.selectedSchemaId}
      setSelectedSchema={(
        schemaId: string | null,
        schemaName: string | null,
        registryId: string | null,
        format: AntibodyFormat | null
      ) => {
        send({type: 'selectSchema', schemaId, schemaName, registryId, format});
      }}
      startingType={ndcSpreadsheetImportModalState.context.selectedStartingType}
      setStartingType={(startingType: NdcStartingType) => {
        send({type: 'setStartingType', startingType});
      }}
      antibodyFormatsQuery={antibodyFormatsQuery}
      germlineConfig={ndcSpreadsheetImportModalState.context.germlineConfig}
      setGermlineConfig={(germlineConfig: NdcGermlineConfig) => {
        send({type: 'setGermlineConfig', germlineConfig});
      }}
      showGermlineSelectors={showGermlineSelectors}
      onGermlineSelected={(metadata) => {
        trackEventWithModule(ANTIBODIES_ANALYTICS_ACTIONS.SELECTED_GERMLINE, metadata);
      }}
    />
  );

  const renderUploadSpreadsheetPane = (): JSX.Element => (
    <UploadSpreadsheetPane
      startingType={ndcSpreadsheetImportModalState.context.selectedStartingType}
      spreadsheetFile={ndcSpreadsheetImportModalState.context.spreadsheetFile}
      setSpreadsheetFile={(spreadsheetFile: File) => {
        send({type: 'setSpreadsheetFile', spreadsheetFile});
      }}
      setSpreadsheetURL={(spreadsheetURL: string) => {
        send({type: 'setSpreadsheetURL', spreadsheetURL});
      }}
      clearSpreadsheet={() => {
        send({type: 'clearSpreadsheet'});
      }}
      columnInferenceErrorMessages={ndcSpreadsheetImportModalState.context.columnInferenceErrorMessages}
      isInferringColumns={ndcSpreadsheetImportModalState.matches({uploadSpreadsheet: 'inferringColumns'})}
      selectedAntibodyFormat={assertNotNullAndReturn(selectedAntibodyFormat)}
    />
  );

  const renderColumnMappingPane = (): JSX.Element => (
    <ColumnMappingPane
      spreadsheetInterpretation={assertNotNullAndReturn(
        ndcSpreadsheetImportModalState.context.spreadsheetInterpretation
      )}
      updateSpreadsheetInterpretation={(spreadsheetInterpretation: NdcSpreadsheetInterpretation) => {
        send({type: 'updateSpreadsheetInterpretation', spreadsheetInterpretation});
      }}
    />
  );

  const renderFinalizePane = (): JSX.Element => (
    <FinalizePane
      projectFolderId={ndcSpreadsheetImportModalState.context.selectedProjectFolderId}
      setProjectFolder={(projectFolderId: string) => {
        send({type: 'setProjectFolder', projectFolderId});
      }}
      relatedSchemasConfig={ndcSpreadsheetImportModalState.context.relatedSchemasConfig}
      setRelatedSchemasConfig={(relatedSchemasConfig: NdcRelatedSchemasConfig) => {
        send({type: 'setRelatedSchemasConfig', relatedSchemasConfig});
      }}
      translationParameters={ndcSpreadsheetImportModalState.context.translationParameters}
      setTranslationParameters={(translationParameters: TranslationParameters) => {
        send({type: 'setTranslationParameters', translationParameters});
      }}
      countsOfCreatedComponents={getCountsOfCreatedComponents(
        assertNotNullAndReturn(ndcSpreadsheetImportModalState.context.spreadsheetInterpretation),
        ndcSpreadsheetImportModalState.context.selectedStartingType,
        assertNotNullAndReturn(selectedAntibodyFormat)
      )}
      selectedAntibodyFormat={assertNotNullAndReturn(selectedAntibodyFormat)}
      selectedSchemaName={assertNotNullAndReturn(ndcSpreadsheetImportModalState.context.selectedSchemaName)}
      componentAndRelatedSchemas={ndcSpreadsheetImportModalState.context.componentAndRelatedSchemas}
      componentAndRelatedSchemasErrorMessage={
        ndcSpreadsheetImportModalState.context.componentAndRelatedSchemasErrorMessage
      }
      selectedDocumentId={ndcSpreadsheetImportModalState.context.selectedDocumentId}
      setSelectedDocumentId={(documentId: string | null) => {
        send({type: 'setSelectedDocumentId', documentId});
      }}
    />
  );

  const renderValidationErrorsPane = (): JSX.Element => (
    <ValidationErrorsPane validationErrors={ndcSpreadsheetImportModalState.context.validationErrors} />
  );

  return (
    <Modal
      title="Create antibodies"
      buttons={getButtons()}
      onRequestSubmit={handleSubmitButton}
      onRequestClose={handleOnRequestClose}
      scrollable={true}
      expandHeight={true}
      large={true}
      bodyPadded={false}
    >
      <div className={classes.modalBody}>
        <div className={classes.stepNav}>
          <StepNav
            steps={stepsToShow}
            statusByStep={statusByStep}
            currentStepKey={currentStep}
            onUpdateCurrentStep={undefined}
            variant={StepNavVariant.STACKED}
            size={StepNavSize.FULL_WIDTH}
          />
        </div>
        {BenchlingConfig.ANTIBODY_NESTED_DATA_CREATION_USE_SPREADSHEET_IMPORT &&
          !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENFORCED_DATA_MODEL && (
            <div className={classes.unsupportedConfigBanner}>
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text="Unsupported configuration"
                supportingInfo="Spreadsheet import is enabled but enforced data model is disabled. This configuration is unsupported. You may proceed at your own risk."
                dataTestAttrs={{
                  component: 'NestedCreateAndRegisterAntibodiesSpreadsheetImportModal',
                  element: 'unsupportedConfigBanner',
                }}
              />
            </div>
          )}
        <div
          {...getTestProps({
            component: 'NestedCreateAndRegisterAntibodiesSpreadsheetImportModal',
            element: 'step-content',
          })}
        >
          {currentStep === AntibodySpreadsheetImportStepKey.SELECT_FORMAT && renderSelectFormatPane()}
          {currentStep === AntibodySpreadsheetImportStepKey.UPLOAD_SPREADSHEET &&
            renderUploadSpreadsheetPane()}
          {currentStep === AntibodySpreadsheetImportStepKey.COLUMN_MAPPING && renderColumnMappingPane()}
          {currentStep === AntibodySpreadsheetImportStepKey.FINALIZE && renderFinalizePane()}
          {currentStep === AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS && renderValidationErrorsPane()}
        </div>
      </div>
    </Modal>
  );
}
